import type { Overmind } from 'overmind';

import type { ApplicationConfiguration } from '@/common/overmind/namespaces/arcgis/config/config.types';
import { getApplicationId } from '@/common/overmind/namespaces/tenants/actions';

import type { Context } from '../..';
import { initializeApis } from './initializeApis';
import { setupAppRouter } from './setupAppRouter';
import { setupReactions } from './setupReactions';

export { setGridCalcApiUrls } from './setGridCalcApiUrls';
export {
  authorize,
  setAuthAsInitialized,
  authInitialized,
  loggedOut
} from './authorize';

export const onInitializeOvermind = async (
  context: Context,
  overmindInstance: Overmind<Context>
) => {
  if (process.env.NODE_ENV === 'development') {
    (window as any).overmind = overmindInstance;
  }
  const { effects, state, actions } = context;
  initializeApis(effects, defaultApiConfig);
  const appConfig = await effects.api.assets.getApplicationConfiguration();
  actions.setApplicationConfig(appConfig);
  actions.setGridCalcApiUrls(appConfig.api);
  context.effects.log.initialize(appConfig.appInsightsConnectionString);
  effects.applicationAccess.initialize(effects.api.identity, () =>
    getApplicationId(state)
  );
  initializeSelectedTenantEffect(effects);
  effects.evaluationRules.initialize(
    () => effects.featureToggleReader.getVariant<"default" | "next" | "api">(
      'pg-anb-grid-calculator-calculation-results-technical-boundaries-adjustment'
    ),
    appConfig.api.orchestratorUrl,
    () => context.state.auth.matches("AUTHENTICATED")?.user.token || ''
  )
  setupAppRouter(context);
  await context.actions.authorize();
  setupReactions(overmindInstance);
  effects.calculationSetExecutionReport.initialize(
    () => context.state.calculation.calculationTactic,
    () => context.state.calculation.calculationContext,
    effects.sharing,
    effects.files
  );

  context.reaction(
    state => [state.arcgis.config.value] as const,
    ([config]) => {
      effects.arcgis.auth.identity.initialize({
        config: () => config,
        tryStoringCalculationContext: () => {
          if(
            state.calculation.externallyTriggeredCalculationSetExecution.current !== 'IDLE'
          ) {
            const calculationContext = state.calculation.externallyTriggeredCalculationSetExecution.calculationContext;
            if(!calculationContext) return
            context.effects.storage.set("calculationContext", calculationContext);
          }
        }
      });
    }
  );
};

export const defaultApiConfig = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=UTF-8'
  }
};

export const setApplicationConfig = (
  { state }: Context,
  config: ApplicationConfiguration
) => {
  state.config = { type: 'AVAILABLE', config };
};
function initializeSelectedTenantEffect(effects: Context['effects']) {
  effects.selectedTenant.initialize({
    storage: {
      getItem<T>(key: string) {
        return localStorage.getItem(key) as T | null;
      },
      setItem(key, value) {
        localStorage.setItem(key, value);
      }
    }
  });
}

export const arcGisTokenReady = (context: Context) => {
  context.state.auth.send('ARCGIS_AUTHENTICATION_COMPLETED');
};
